import { isNumber, isString } from 'fitify-types/src/type-guards/common'
import { useRunOnce } from 'fitify-ui/src/hooks/useRunOnce'
import { parseInteger } from 'fitify-utils/src/properties'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { type ExerciseListItem, getMuscleI18nKey } from '@/utils/exercises'

const ALL_VALUE = 'all'

const EXERCISES_PER_PAGE = 18

export type UseExercisesProps = {
  exercises: ExerciseListItem[]
}

export const useExercises = ({ exercises }: UseExercisesProps) => {
  const router = useRouter()
  const { t } = useTranslation()

  // Initialize state from URL query params
  const [search, setSearch] = useState<string>('')
  const [equipmentFilter, setEquipmentFilter] = useState<string>(ALL_VALUE)
  const [muscleFilter, setMuscleFilter] = useState<string>(ALL_VALUE)
  const [page, setPage] = useState<number>(1)

  // Sync state from URL once router is ready
  useRunOnce(() => {
    if (router.isReady) {
      if (isString(router.query.search)) {
        setSearch(router.query.search)
      }
      if (isString(router.query.equipment)) {
        setEquipmentFilter(router.query.equipment)
      }
      if (isString(router.query.muscle)) {
        setMuscleFilter(router.query.muscle)
      }
      if (isString(router.query.page)) {
        const newPage = parseInteger(router.query.page)
        if (isNumber(newPage) && newPage >= 1) {
          setPage(newPage)
        }
      }
    }
  }, router.isReady)

  // Update URL query params when filters or page change
  useEffect(() => {
    if (router.isReady) {
      const newQuery = {
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(equipmentFilter !== ALL_VALUE
          ? { equipment: equipmentFilter }
          : {}),
        ...(muscleFilter !== ALL_VALUE ? { muscle: muscleFilter } : {}),
        ...(page > 1 ? { page: String(page) } : {}),
      }
      void router.replace(
        { pathname: router.pathname, query: newQuery },
        undefined,
        {
          shallow: true,
          scroll: false,
        }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, equipmentFilter, muscleFilter, page])

  const handleFilterChange = useCallback(
    (setter: (value: string) => void) => (value: string) => {
      setter(value)
      setPage(1)
    },
    []
  )

  const equipmentOptions = useMemo(() => {
    return [
      ALL_VALUE,
      'bodyweight',
      'chair',
      'dumbbell',
      'barbell',
      'kettlebell',
      'resistanceband',
      'trx',
      'bosu',
      'swissball',
      'medicineball',
      'pullupbar',
      'foamroller',
      'yoga',
    ].map((value) => ({
      label:
        value === ALL_VALUE
          ? t('landing_exercises_filter_all')
          : t(`landing_exercises_tool_${value}`),
      value,
    }))
  }, [t])

  const muscleOptions = useMemo(() => {
    return [
      ALL_VALUE,
      'abs',
      'chest',
      'biceps',
      'triceps',
      'quads',
      'glutes',
      'lats',
      'shoulders_front',
      'hams',
      'calves',
    ].map((value) => ({
      label:
        value === ALL_VALUE
          ? t('landing_exercises_filter_all')
          : t(getMuscleI18nKey(value)),
      value,
    }))
  }, [t])

  const { filteredExercises, totalExercises, totalPages } = useMemo(() => {
    let filteredExercises = exercises

    if (search.trim()) {
      const lowerCaseSearch = search.trim().toLowerCase()
      filteredExercises = filteredExercises.filter((exercise) =>
        exercise.title.toLowerCase().includes(lowerCaseSearch)
      )
    }

    if (equipmentFilter !== ALL_VALUE) {
      filteredExercises = filteredExercises.filter(
        (exercise) => (exercise.tool || 'bodyweight') === equipmentFilter
      )
    }

    if (muscleFilter !== ALL_VALUE) {
      filteredExercises = filteredExercises.filter((exercise) => {
        const intensity =
          exercise.muscle_intensity?.[
            muscleFilter as keyof typeof exercise.muscle_intensity
          ]
        return intensity !== undefined && intensity >= 3
      })
    }

    return {
      filteredExercises,
      totalExercises: filteredExercises.length,
      totalPages: Math.ceil(filteredExercises.length / EXERCISES_PER_PAGE),
    }
  }, [equipmentFilter, exercises, muscleFilter, search])

  const paginatedExercises = useMemo(() => {
    return filteredExercises.slice(
      (page - 1) * EXERCISES_PER_PAGE,
      page * EXERCISES_PER_PAGE
    )
  }, [filteredExercises, page])

  return {
    equipmentFilter,
    equipmentOptions,
    handleFilterChange,
    muscleFilter,
    muscleOptions,
    page,
    paginatedExercises,
    search,
    setEquipmentFilter,
    setMuscleFilter,
    setPage,
    setSearch,
    totalExercises,
    totalPages,
  }
}
